import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Sidebar from './Sidebar';
import OfflineBanner from '../ui/OfflineBanner';
import { InstallPrompt } from '../ui/InstallPrompt';
import UnlockEncryption from '../auth/UnlockEncryption';
import { ReconsentModal } from '../auth/ReconsentModal';

// Lazy so first-run onboarding doesn't ship to users who've already completed it.
const WelcomeWizard = lazy(() => import('../onboarding/WelcomeWizard'));
import { connectSocket, disconnectSocket } from '../../lib/socket';
import { useAuthStore } from '../../stores/authStore';
import { useCryptoStore } from '../../stores/cryptoStore';
import { usePresenceStore } from '../../stores/presenceStore';
import { SidebarContext } from '../../lib/sidebarContext';
import { SocketEvents, epochKeyRing } from '@clinikchat/shared';
import { requestNotificationPermission, showBrowserNotification } from '../../lib/notifications';
import { playMessageChime } from '../../lib/chime';
import { subscribeToWebPush } from '../../lib/web-push';

export default function AppLayout() {
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((s) => s.accessToken);
  const setPresence = usePresenceStore((s) => s.setStatus);
  const { channelId: activeChannelId } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const activeChannelIdRef = useRef(activeChannelId);
  // When Web Push is live the service worker raises an OS notification for
  // every message, so the in-page path must stand down or the user gets two.
  const pushActiveRef = useRef(false);

  // Keep ref in sync without reconnecting the socket
  useEffect(() => {
    activeChannelIdRef.current = activeChannelId;
    setSidebarOpen(false);
  }, [activeChannelId]);

  // The service worker cannot resolve a push without showing a notification
  // (userVisibleOnly), so it always shows one and then tells on-screen tabs.
  // If this tab has focus the user is already here — dismiss it rather than
  // leaving an alert for a conversation they are looking at.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const handlePushShown = (event: MessageEvent) => {
      const data = event.data as { type?: string; tag?: string } | null;
      if (!data || data.type !== 'clinikchat-push' || !data.tag) return;
      if (!document.hasFocus()) return;
      void navigator.serviceWorker.ready
        .then((registration) => registration.getNotifications({ tag: data.tag }))
        .then((notifications) => notifications.forEach((n) => n.close()))
        .catch(() => undefined);
    };

    navigator.serviceWorker.addEventListener('message', handlePushShown);
    return () => navigator.serviceWorker.removeEventListener('message', handlePushShown);
  }, []);

  useEffect(() => {
    if (accessToken) {
      requestNotificationPermission();
      // Register for Web Push so messages still reach the user with no tab
      // open. Idempotent and non-blocking — failures are reported by return
      // value, never thrown, so notification setup can't break the app.
      void subscribeToWebPush().then((result) => {
        pushActiveRef.current = result === 'subscribed';
      });

      const socket = connectSocket();

      const handlePresence = (data: { userId: string; status: 'ONLINE' | 'AWAY' | 'OFFLINE' }) => {
        setPresence(data.userId, data.status);
      };

      const handleNewMessageNotification = (msg: {
        channelId: string;
        content: string;
        encrypted?: boolean;
        user?: { displayName: string };
      }) => {
        const isActiveChannel = msg.channelId === activeChannelIdRef.current;
        // Audible cue, deliberately outside the pushActiveRef gate below: the
        // push-raised OS notification is silent, so without this chime a
        // push-subscribed reception desk hears nothing at all.
        if (!isActiveChannel || document.hidden) {
          playMessageChime();
        }
        if (!isActiveChannel) {
          // The service worker already raises an OS notification for this same
          // message whenever push is subscribed, and it carries the channel id
          // needed to route a click. Raising a second one here would stack a
          // duplicate on any tab that is open but unfocused.
          if (pushActiveRef.current) return;
          // Never pass message content to the OS notification layer — encrypted
          // channels would expose ciphertext as the body, and even for plaintext
          // channels this would leak PHI to the OS notification centre / lock
          // screen. Show a generic prompt instead.
          showBrowserNotification(msg.user?.displayName || 'New message', 'New message received');
        }
      };

      const handleChannelDelete = (payload: { channelId: string; orgId?: string }) => {
        // Deleted, or this user's membership was removed (the API sends
        // CHANNEL_LEAVE to their sockets). Refresh every sidebar/channel query
        // that might still list the channel; the open ChannelView handles its
        // own navigation.
        void queryClient.invalidateQueries({ queryKey: ['channels'] });
        // Forget the channel's keys in memory: a removed member who is re-added with
        // "new messages only" must not keep decrypting the older epochs from this tab.
        epochKeyRing.clear(payload.channelId);
        // ...and the legacy flat key: decryptChannelMessage falls back to it when the
        // ring misses an epoch, so leaving it would defeat the clear above.
        useCryptoStore.getState().removeChannelKey(payload.channelId);
        void queryClient.removeQueries({ queryKey: ['channel', payload.channelId] });
        void queryClient.removeQueries({ queryKey: ['messages', payload.channelId] });
      };

      // Emitted by the API when this user is added to a channel or a peer
      // opens a DM with them. Without it a brand-new DM (or a channel someone
      // else added them to) never showed in the sidebar until a full reload —
      // the API has emitted this since the #77 add-member fix, but nothing
      // was listening.
      const handleChannelJoin = () => {
        void queryClient.invalidateQueries({ queryKey: ['channels'] });
      };

      const handleSocketError = (payload: { message?: string }) => {
        toast.error(payload.message?.trim() || 'A realtime update failed. Please try again.');
      };

      socket.on(SocketEvents.PRESENCE_UPDATE, handlePresence);
      socket.on(SocketEvents.MESSAGE_NEW, handleNewMessageNotification);
      socket.on(SocketEvents.CHANNEL_DELETE, handleChannelDelete);
      socket.on(SocketEvents.CHANNEL_LEAVE, handleChannelDelete);
      socket.on(SocketEvents.CHANNEL_JOIN, handleChannelJoin);
      socket.on(SocketEvents.ERROR, handleSocketError);

      return () => {
        socket.off(SocketEvents.PRESENCE_UPDATE, handlePresence);
        socket.off(SocketEvents.MESSAGE_NEW, handleNewMessageNotification);
        socket.off(SocketEvents.CHANNEL_DELETE, handleChannelDelete);
        socket.off(SocketEvents.CHANNEL_LEAVE, handleChannelDelete);
        socket.off(SocketEvents.CHANNEL_JOIN, handleChannelJoin);
        socket.off(SocketEvents.ERROR, handleSocketError);
        disconnectSocket();
      };
    }
    return undefined;
  }, [accessToken, setPresence]);

  return (
    <SidebarContext.Provider value={{ openSidebar: () => setSidebarOpen(true) }}>
      <div className="flex h-screen overflow-hidden bg-warm-white">
        <OfflineBanner />
        <InstallPrompt />
        <ReconsentModal />

        {/* Signed in but no private key (page refresh, PWA restart, or arrived
            via register/join, none of which initialise keys). Blocks the app
            until the key is unwrapped, instead of letting every encrypt and
            decrypt fail silently. */}
        <UnlockEncryption />

        {/* Mobile backdrop — warm semi-transparent overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-warm-gray-900/40 backdrop-blur-sm md:hidden transition-opacity duration-200"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar — slide-over on mobile, static on desktop */}
        <div
          className={`fixed inset-y-0 left-0 z-40 flex-shrink-0 transition-transform duration-200 ease-in-out md:static md:z-auto md:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <Sidebar onClose={() => setSidebarOpen(false)} />
        </div>

        {/* Soft border between sidebar and content */}
        <div className="hidden md:block w-px bg-warm-border" />

        <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-warm-white dark:bg-warm-dark-bg">
          <Outlet />
        </main>

        {/* First-run onboarding; renders only when user.tutorialCompletedAt is null. */}
        <Suspense fallback={null}>
          <WelcomeWizard />
        </Suspense>
        {/* <Toaster> lives in main.tsx so toasts also render on /login etc. */}
      </div>
    </SidebarContext.Provider>
  );
}
